import { Container } from './Container'
import { Eyebrow } from './Eyebrow'
import { formatPlayedOn, formatWeekday } from '../../lib/golfFormat'
import type { PageStat } from './PageHead'

export interface RoundHeadProps {
  /** The course as the card names it. Null only if the round carries none. */
  courseName: string | null
  /** `played_on` — DATE-ONLY, and parsed as such (see `formatPlayedOn`). */
  playedOn: string | null
  holesCount: number | null
  /**
   * Seats on the card — or NULL while the club has read no round: a visitor,
   * a dead id, a failed read. Null prints no count at all; a `0` here would be
   * the head asserting an empty fourball about a round it never opened.
   */
  playersCount: number | null
  /**
   * `posted_at !== null` — clubhouse visibility, and nothing else. NULL, like
   * `playersCount`, until a round has actually been read: the chip is a
   * statement about the round's visibility, and "Private" over a plate that
   * says "no such round" is a claim the club cannot back.
   */
  posted: boolean | null
  /** The scorekeeper, when the round names one. */
  keeperName?: string | null
  /**
   * Stats the SCREEN derived and can stand behind — a low gross, a count of
   * birdies. Holes and players are this component's own because they are on
   * every round; anything computed from summaries belongs to the caller, which
   * is the only place that knows whether a summary arrived.
   */
  extraStats?: readonly PageStat[]
}

/**
 * The opening of one round's page: whose day it was, where, and whether the
 * club can see it.
 *
 * Deliberately NOT `PageHead`. That component's title is a sentence with an
 * optional italic tail and no room for a chip, and a round's headline is a chip
 * sentence — "Saturday at *Kissena*. [Posted]". Rather than widen the shared
 * head for one screen's furniture, this is its own opening in the same
 * stationery: same contour band, same eyebrow, same Fraunces h1, same brass
 * rule over the statistics.
 *
 * ⚠️ The `h1` lives here rather than in the screen, and that is load-bearing:
 * `usePageTitle` moves focus to `main h1` on every route change, and the head
 * renders OUTSIDE the screen's `BookGate` so a visitor, a lapsed member and a
 * dead id all still land on a heading rather than on nothing.
 *
 * Two states, and only the two the card draws: READ (the chip, the band) and
 * UNREAD (neither). There is deliberately no loading state, and the screen
 * does not stand the unread head in for one — it mounts this head only once
 * the read has settled, so a page never opens on "A round" and then grows a
 * band under the reader's eye.
 */
export function RoundHead({
  courseName,
  playedOn,
  holesCount,
  playersCount,
  posted,
  keeperName = null,
  extraStats = [],
}: RoundHeadProps) {
  const stats: PageStat[] = [
    ...(holesCount === null ? [] : [{ value: String(holesCount), label: 'Holes' }]),
    ...(playersCount === null
      ? []
      : [{ value: String(playersCount), label: playersCount === 1 ? 'Player' : 'Players' }]),
    ...extraStats,
  ]

  return (
    <section className="relative overflow-hidden pb-10 pt-14" data-round-head="">
      {/* The clubhouse contour band, at PageHead's own proportions. */}
      <svg
        viewBox="0 0 1200 320"
        preserveAspectRatio="none"
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0 size-full opacity-40"
      >
        <path d="M-50 250 Q300 228 620 258 T1250 240" stroke="var(--color-sage)" fill="none" strokeWidth="1.3" />
        <path d="M-50 278 Q300 256 620 286 T1250 268" stroke="var(--color-brass)" fill="none" strokeWidth="1" opacity=".45" />
        <path d="M-50 304 Q320 282 640 312 T1250 296" stroke="var(--color-fairway)" fill="none" strokeWidth="1.3" opacity=".55" />
      </svg>

      <Container>
        <Eyebrow>The Clubhouse · The Book · {formatPlayedOn(playedOn)}</Eyebrow>
        <h1 className="mb-3.5 mt-4 font-display text-[clamp(2.125rem,4.4vw,3.25rem)] font-medium leading-[1.05] tracking-[-0.015em] text-pine">
          {/* No course, no "at": a round the club could not name a ground for
              is "A round", never a sentence with a hole where the place was. */}
          {courseName ? (
            <>
              {headlineFor(playedOn)}
              <em className="italic text-fairway"> {courseName}</em>
            </>
          ) : (
            'A round'
          )}
          {posted !== null && (
            <span className="ml-3.5 inline-block align-middle">
              <PostedChip posted={posted} />
            </span>
          )}
        </h1>
        <p className="max-w-[56ch] text-[16.5px] text-ink-soft">{ledeFor(playersCount, holesCount, keeperName)}</p>

        {/* No rule over nothing: a head with no round to count renders no
            statistics band, rather than a brass line over an empty list. */}
        {stats.length > 0 && (
          <dl className="mt-9 flex max-w-[680px] flex-wrap gap-x-12 gap-y-5 border-t border-brass/35 pt-6">
            {stats.map((stat) => (
              <div key={stat.label}>
                <dd className="font-display text-[28px] tabular-nums text-pine">{stat.value}</dd>
                <dt className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-soft">
                  {stat.label}
                </dt>
              </div>
            ))}
          </dl>
        )}
      </Container>
    </section>
  )
}

/**
 * Posted or private, said in the club's own words.
 *
 * `posted_at` is the ONLY visibility flag on the wire, and a private round is
 * an ordinary state rather than a fault — a card still counts toward an index
 * whether or not the clubhouse can read it. So the chip is a statement, not a
 * warning, and it never wears the flag red.
 */
function PostedChip({ posted }: { posted: boolean }) {
  return (
    <span
      data-posted-chip={posted ? 'posted' : 'private'}
      className={
        posted
          ? 'rounded-club-sm border border-sage bg-parchment px-2 py-1 text-[9.5px] font-semibold uppercase tracking-[0.2em] text-fairway'
          : 'rounded-club-sm border border-ink-soft/35 bg-parchment px-2 py-1 text-[9.5px] font-semibold uppercase tracking-[0.2em] text-ink-soft'
      }
    >
      {posted ? 'Posted' : 'Private'}
    </span>
  )
}

/**
 * "Saturday at" — the weekday the round was played, or a plain opener when the
 * club was never told a date.
 *
 * `formatWeekday` does the naming, and this used to keep a private English
 * `WEEKDAYS` table beside it. Two weekday tables in one club is one too many:
 * the shared one is `Intl`, so this head and `ShareHero` name the same day in
 * the same words, and the UTC-midnight trap that names the day BEFORE
 * everywhere west of Greenwich is guarded in exactly one place.
 */
function headlineFor(playedOn: string | null): string {
  const weekday = formatWeekday(playedOn)
  return weekday === null ? 'A round at' : `${weekday} at`
}

/** What every round's page holds — the one sentence the head can say unread. */
const WHAT_THE_PAGE_HOLDS =
  "Every stroke as it was written — the card, the day's stories, and the holes as they were played."

/**
 * The lede counts the round only once there is one. With no round read there
 * is no card count, no hole count and no keeper to name — it used to open
 * "0 cards" over a plate saying no such round exists.
 */
function ledeFor(players: number | null, holes: number | null, keeper: string | null): string {
  if (players === null) return WHAT_THE_PAGE_HOLDS
  const cards = players === 1 ? 'One card' : `${players} cards`
  const played = holes === null ? '' : `, ${holes} holes`
  const kept = keeper ? `, kept by ${keeper}` : ''
  return `${cards}${played}${kept}. ${WHAT_THE_PAGE_HOLDS}`
}
